import { IQuantity } from 'generated/graphql-gateway';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import { isEntryAvailable, isItem } from 'state/unified-menu/utils';

import { SlotSelections } from '../../product-wizard/types';
import { ModifierType } from '../modifier/types';
import { mapModifier } from '../modifier/utils';
import { QUANTITY_DEFAULTS } from '../utils';

export interface ModifierInformation {
  modifier: ILocalizedMenuEntry;
  modifierRemainingSelections: number;
  modifierQuantity?: IQuantity | null;
}

/**
 * Returns the modifier that require user selections,
 * and computes the remaining selections amount
 */
export const getRequiredModifierInformation = (
  menu: IndexedMenu<ILocalizedMenuEntry>,
  itemId: string,
  slotSelection: SlotSelections | undefined,
  posServiceMode: PosServiceMode
): ModifierInformation | null => {
  const item = menu.find(itemId);
  if (
    !item ||
    !isItem(item) ||
    item.options?.entries?.length !== 1 ||
    !isEntryAvailable(item.options.entries[0], posServiceMode, undefined)
  ) {
    return null;
  }

  const [{ entryId: modifierKey, quantity: modifierQuantity }] = item.options.entries;

  const mappedModifier = mapModifier(
    menu,
    modifierKey,
    item.options?.entries.length ?? 0,
    posServiceMode
  );

  const modifierQtys = Object.values(slotSelection?.modifiers?.[modifierKey] ?? {});

  if (!modifierQtys.length) {
    // no user selections, however, user interaction might not be required
    if (mappedModifier && mappedModifier.type === ModifierType.CHECKBOX) {
      modifierQtys.push(1);
    }
  }

  let selectedQuantity = 0;
  for (const quantity of modifierQtys) {
    selectedQuantity += quantity;
  }

  const quantityConstraints = modifierQuantity ?? {
    min: QUANTITY_DEFAULTS.min,
    max: QUANTITY_DEFAULTS.max,
  };
  const modifierMinAmount = quantityConstraints.min ?? 0;
  const remaining = modifierMinAmount - selectedQuantity;

  const modifier = menu.get(modifierKey);
  return {
    modifier,
    modifierRemainingSelections: Math.max(remaining, 0),
    modifierQuantity: quantityConstraints,
  };
};
